import { Card, Flex, SegmentedControl, Separator, Text, TextField } from '@hubertai/hubert-component-library';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TAvailableLanguageCodes } from '@hubertai/screening-question-types';
import type { TRuleEditorCommonProps } from '../types';
import LanguageCombobox from 'components/comboboxes/LanguageCombobox';
import {
  EInterviewQualified,
  HubertQualifiedSelectList,
} from 'components/HubertUI/EvaluationStatus/HubertQualifiedSelect/HubertQualifiedSelect.constants';
import { EInterviewProperty } from 'components/Selects/InterviewPropertySelect/InterviewPropertySelect.constants';
import type { TQuestionRuleCondition } from 'types/rules.types';

type TQualifiedState = NonNullable<TQuestionRuleCondition['fallback']>['value'];

interface Props extends TRuleEditorCommonProps {
  condition: TQuestionRuleCondition;
  onChange: (patch: Partial<TQuestionRuleCondition>) => void;
  // Scopes the language picker to a fixed set (e.g. a template's own configured languages) instead
  // of every language the org supports — omit to show the full list, which is the only option that
  // makes sense for a question edited standalone, outside any template's language settings.
  allowedLanguages?: TAvailableLanguageCodes[];
}

// Radix Card clips its content (overflow + contain: paint), which hides the language picker's
// dropdown since it isn't portaled. isolation keeps the stacking context the card's background needs.
const Panel = ({ children }: { children: ReactNode }) => (
  <Card size="2" style={{ overflow: 'visible', contain: 'none', isolation: 'isolate' }}>
    {children}
  </Card>
);

const QualifiedControl = ({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled?: boolean;
  onChange: (value: TQualifiedState) => void;
}) => (
  <SegmentedControl.Root
    size="1"
    value={value}
    disabled={disabled}
    onValueChange={(next) => onChange(next as TQualifiedState)}
  >
    {HubertQualifiedSelectList.map((item) => (
      <SegmentedControl.Item key={item.value} value={item.value}>
        {item.label}
      </SegmentedControl.Item>
    ))}
  </SegmentedControl.Root>
);

// Commits on blur, so typing "70" is one rule change rather than one per keystroke.
const ThresholdField = ({
  label,
  value,
  disabled,
  onCommit,
}: {
  label: string;
  value: number;
  disabled?: boolean;
  onCommit: (value: number) => void;
}) => {
  const [draft, setDraft] = useState(String(value));
  const [committed, setCommitted] = useState(value);
  if (committed !== value) {
    setCommitted(value);
    setDraft(String(value));
  }
  const commit = () => {
    const next = Number(draft);
    if (draft.trim() === '' || !Number.isFinite(next) || next < 0) return setDraft(String(value));
    if (next !== value) onCommit(next);
  };
  return (
    <TextField.Root
      size="1"
      type="number"
      min={0}
      className="w-20"
      aria-label={label}
      disabled={disabled}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === 'Enter' && commit()}
    />
  );
};

const RuleConditionByInterviewProperty = ({ condition, isReadOnly, onChange, allowedLanguages }: Props) => {
  const { t } = useTranslation('Templates');

  if (condition.property === EInterviewProperty.Qualified) {
    return (
      <Panel>
        <Flex align="center" gap="2" wrap="wrap">
          <Text size="2">{t('question.ruleConditions.candidateIs')}</Text>
          <QualifiedControl
            value={String(condition.value)}
            disabled={isReadOnly}
            onChange={(value) => onChange({ value })}
          />
        </Flex>
      </Panel>
    );
  }

  if (condition.property === EInterviewProperty.Language) {
    return (
      <Panel>
        <Flex direction="column" gap="2">
          <Text size="2">{t('question.ruleConditions.languageIsAnyOf')}</Text>
          <LanguageCombobox
            isMulti
            disabled={isReadOnly}
            allowLanguages={allowedLanguages}
            value={
              condition.value
                ? (Array.isArray(condition.value) ? condition.value : [condition.value]).map(String)
                : undefined
            }
            onValueChange={(value) => onChange({ value })}
          />
        </Flex>
      </Panel>
    );
  }

  if (condition.property === EInterviewProperty.TotalScore) {
    const fallback = condition.fallback ?? { property: 'qualified' as const, value: EInterviewQualified.Qualified };
    return (
      <Panel>
        <Flex direction="column" gap="3">
          <Flex align="center" gap="2" wrap="wrap">
            <Text size="2">{t('question.ruleConditions.qualifiedAndScoreAtLeast')}</Text>
            <ThresholdField
              label="Minimum total score"
              value={Number(condition.value) || 0}
              disabled={isReadOnly}
              onCommit={(value) => onChange({ value })}
            />
          </Flex>
          <Separator size="4" />
          <Flex direction="column" gap="2">
            <Text size="1" weight="medium" tone="muted" className="tracking-wide uppercase">
              {t('question.ruleConditions.untilScoresReady')}
            </Text>
            <Flex align="center" gap="2" wrap="wrap">
              <Text size="2">{t('question.ruleConditions.candidateIs')}</Text>
              <QualifiedControl
                value={fallback.value}
                disabled={isReadOnly}
                onChange={(value) => onChange({ fallback: { ...fallback, value } })}
              />
              <Text size="2">{t('question.ruleConditions.andBonusAtLeast')}</Text>
              <ThresholdField
                label="Minimum bonus points"
                value={fallback.min_bonuspoints ?? 0}
                disabled={isReadOnly}
                onCommit={(min_bonuspoints) => onChange({ fallback: { ...fallback, min_bonuspoints } })}
              />
            </Flex>
            <Text size="1" tone="muted">
              {t('question.ruleConditions.scoreGateHint')}
            </Text>
          </Flex>
        </Flex>
      </Panel>
    );
  }

  return null;
};

export default RuleConditionByInterviewProperty;
